import { Box, Button, CircularProgress, Grid, Stack, SvgIcon, Tab, Tabs, useMediaQuery } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { Item, ItemRequest, PagedData_1OfWasteHeaders, WasteCheckSheetRequest } from '../../app/services/api/generated';
import { UserContext } from '../../components/shared/useUser';
import { useTranslation } from 'react-i18next';
import { Plus, SearchSm } from './../../assets';
import { WasteChecksheetTab } from './enums/WasteChecksheetTab';
import useNswagClient from '../../hooks/api/useNswagClient';
import { WasteChecksheetFilterOptions } from './interfaces/WasteChecksheetFilterOptions';
import { useNavigate } from 'react-router-dom';
import useLogError from '../../hooks/useLogError';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import PageTitle from '../../components/shared/PageTitle';
import PageArea from '../../components/shared/PageArea';
import { theme } from '../../theme';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import DatePickerLocalized from '../../components/shared/DatePickerLocalized';
import { Section } from '../../components/ui/Section';
import WasteChecksheetTable from './components/WasteChecksheetTable';

const WasteChecksheetPage = () => {
  const [wasteChecksheet, setWasteChecksheet] = useState<PagedData_1OfWasteHeaders>({});
  const [activeTab, setActiveTab] = useState<WasteChecksheetTab>(WasteChecksheetTab.ACTIVE);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [date, setDate] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('createdDate');
  const [itemsForCreateNewChecksheet, setItemsForCreateNewChecksheet] = useState<Item[]>([]);

  const { t } = useTranslation('wasteChecksheet');
  const { hasPermissionTo, selectedStore, isAdmin } = useContext(UserContext);
  const { getWaste, getItems, createWaste } = useNswagClient();
  const navigate = useNavigate();
  const { logError } = useLogError();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const hasPermissionToCreateNew = hasPermissionTo(['DailyWasteCheckSheetWrite']) || isAdmin();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  const [openModal, setOpenModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showDeleteMessge, setShowDeleteMessge] = useState(false);
  const [lastSearchedDate, setLastSearchedDate] = useState('');
  const [deleteId, setDeleteId] = useState(-1);
  const { deleteWaste } = useNswagClient();

  useEffect(() => {
    loadFilterData({
      type: WasteChecksheetTab.ACTIVE,
      date: date,
      skip: 0,
      take: 10,
    });

    getCurrencyCodeConfiguration();
    getItems(undefined, selectedStore?.storeNumber, true, undefined, undefined, undefined, undefined, undefined, undefined, true, undefined, 'ItemNumber')
      .then((result) => {
        setItemsForCreateNewChecksheet(result.data?.items ?? []);
      }).catch((error) => {
        logError(error);
      });
  }, []);

  useEffect(() => {
    loadFilterData({
      type: activeTab,
      date: date,
      skip: page * 10,
      take: 10,
    });

    getCurrencyCodeConfiguration();
  }, [page, order, orderBy]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: WasteChecksheetTab) => {
    setActiveTab(newValue);
    setPage(0);
    setDate('');
    setLastSearchedDate('');

    loadFilterData({
      type: newValue,
      date: '',
      skip: 0,
      take: 10,
    });
  };

  const loadFilterData = (options: WasteChecksheetFilterOptions) => {
    setIsLoading(true);
    const {
      date,
      skip,
      take,
      type,
    } = options;

    const storeNumber = selectedStore?.storeNumber;

    getWaste(storeNumber, date, skip, take, type, orderBy, order)
      .then((result) => {
        if (result?.data) {
          setWasteChecksheet(result.data);
        }

      })
      .catch((error) => {
        logError(error);
      })
      .finally(() => setIsLoading(false));
  };

  const navigateToDailyWasteCheckSheet = () => {
    const filterItems: ItemRequest[] = [{
      id: itemsForCreateNewChecksheet[0].id ?? 0,
      itemNumber: itemsForCreateNewChecksheet[0].itemNumber ?? '',
      description: itemsForCreateNewChecksheet[0].description ?? '',
      uom: itemsForCreateNewChecksheet[0].uom ?? '',
      pastDueQty: 0,
      sampledQty: 0,
      damagedQty: 0,
      storeUseQty: 0,
    }];

    const requestData: WasteCheckSheetRequest = {
      id: 0,
      storeNumber: selectedStore?.storeNumber ?? '',
      items: filterItems,
    };

    setIsCreating(true);
    createWaste(requestData)
      .then((result) => {
        if (result) {
          const createdId = result.data;
          navigate(`/store/daily-waste-checksheet/${createdId}`);
        }
      })
      .finally(() => { setIsCreating(false); });
  };

  const handleConfirmDeleteModal = async () => {
    deleteWaste(deleteId).catch((error) => {
      logError(error);
    }).finally(() => {
      setShowDeleteMessge(true);
      setOpenModal(false);
      if (page !== 0) {
        setPage(0);
      } else {
        loadFilterData({
          type: activeTab,
          date: date,
          skip: 0,
          take: 10,
        });
      }
    });
  };

  const defaultTake = 10;
  const defaultSkip = 0;

  useEffect(() => {
    setDate('');
  }, [activeTab]);

  const callLoadDataFromAPI = (options: WasteChecksheetFilterOptions) => {
    loadFilterData(options);
  };

  const clearAll = () => {
    setDate('');
    callLoadDataFromAPI({
      date: '',
      skip: 0,
      take: 10,
      type: activeTab,
    });

    setPage(0);
  };

  const handleDateChange = (newDate: string | null) => {
    setDate(newDate ?? '');
  };

  const searchClick = () => {
    const formattedDate = date != '' ? new Date(date).toISOString() : '';
    callLoadDataFromAPI({
      date: formattedDate,
      skip: defaultSkip,
      take: defaultTake,
      type: activeTab,
    });

    setLastSearchedDate(date);

    setPage(0);
  };

  const handleEnterKey = (event: React.KeyboardEvent<HTMLDivElement> | React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter') {
      searchClick();
    }
  };

  return (
    <PageArea>
      <Box display="flex"
        width="100%"
        height="100%"
        flexDirection="column" >
        <Grid container>
          <Stack width='100%'
            spacing={2}
            direction={isMobile ? 'column' : 'row'}
            sx={{
              justifyContent: 'space-between',
            }}>
            <Box width={isMobile ? '100%' : 'auto'}>
              <PageTitle subTitle='Record and track your waste'>{t('title')}</PageTitle>
            </Box>
            {hasPermissionToCreateNew && (
              <Box width={isMobile ? '100%' : 'auto'}
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                }}>
                <Button
                  variant="primary"
                  size="lg"
                  sx={{
                    mb: { xs: 4, md: 0 },
                  }}
                  onClick={navigateToDailyWasteCheckSheet}
                  startIcon={isCreating ? <CircularProgress size={20}
                    sx={{ ml: '-4px', mr: '4px' }} /> : <SvgIcon><Plus fill="white" /></SvgIcon>}
                  disabled={itemsForCreateNewChecksheet.length == 0 || isCreating}
                >
                  {t('createNewBtn')}
                </Button>
              </Box>
            )}
          </Stack>

          <Grid container
            sx={{ pt: '6px', pb: '12px' }}
            spacing={4}>
            <Grid item
              lg={3}
              sm={4}
              xs={12}>
              <DatePickerLocalized label={t('list.createdDate')}
                useCalendar={false}
                value={date}
                onChange={handleDateChange}
                translations={{
                  clearButtonLabel: '',
                  todayButtonLabel: '',
                  cancelButtonLabel: '',
                  okButtonLabel: '',
                }}/>
            </Grid>
            <Grid item
              lg={2}
              sm={2}
              xs={12}>
              <Button
                variant="secondary"
                size="lg"
                onClick={clearAll}
                fullWidth
              >
                {t('filterPanel.clear')}
              </Button>
            </Grid>
            <Grid item
              lg={2}
              sm={2}
              xs={12}
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right' }}>
              <Button
                variant="primary"
                size="lg"
                startIcon={<SvgIcon> <SearchSm /> </SvgIcon>}
                onClick={searchClick}
                fullWidth>
                {t('filterPanel.search')}
              </Button>
            </Grid>
          </Grid>

          <Grid item
            xs={12}>
            <Tabs value={activeTab}
              onChange={handleTabChange}>
              <Tab
                label={t('list.activeSheet')}
                value={WasteChecksheetTab.ACTIVE} />
              <Tab
                label={t('list.archiveSheet')}
                value={WasteChecksheetTab.ARCHIVED} />
            </Tabs>
          </Grid>

          <Grid item
            xs={12}
            sx={{ mt: '12px' }}>
            {wasteChecksheet &&
              <Section>
                {
                  <WasteChecksheetTable
                    list={wasteChecksheet}
                    activeTab={activeTab}
                    currencyCode={currencyCode}
                    page={page}
                    setPage={setPage}
                    order={order}
                    setOrder={setOrder}
                    orderBy={orderBy}
                    setOrderBy={setOrderBy}
                    date={lastSearchedDate}
                    clearAll={clearAll}
                    setDeleteId={setDeleteId}
                    setOpenModal={setOpenModal}
                    openModal={openModal}
                    handleConfirmDeleteModal={handleConfirmDeleteModal}
                    isLoading={isLoading}
                    navigateToDailyWasteCheckSheet={navigateToDailyWasteCheckSheet}
                    isCreating={isCreating}
                    onAddIsDisabled={itemsForCreateNewChecksheet.length == 0 || isCreating}
                  />
                }
              </Section >
            }
          </Grid>
        </Grid >
      </Box >
      <MessagesSnackbar open={showDeleteMessge}
        onClose={() => { setShowDeleteMessge(false); }}
        message={'Waste Checksheet deleted successfully'}
        severity='success'
        duration={15000} />
    </PageArea >
  );
};

export default WasteChecksheetPage;